import type { JsonApiResponse, JsonApiResponseSingle } from 'json-api-normalizer';

import type { AdviceRoomTranscriptionsPostOptions } from 'api/adviceRooms/adviceRoom/transcriptions';
import api from 'api/apiInstance';
import type { GetOptions } from 'api/types';
import { convertJsonToFormData, mapFormData, mapFormDataDeep } from 'helpers/formData';

import { guestsAdviceRoomsDocumentsApi } from './documents';
import { guestsAdviceRoomsDocumentApi } from './documents/document';
import { guestClientMeetingRoomsDocumentSetsApi } from './documentSets';
import { guestClientMeetingRoomsPossibleParticipantsApi } from './possibleParticipants';

export interface GuestClientMeetingRoomsGetOptions extends GetOptions {
  authToken: string;
}

export function guestClientMeetingRoomsApi(path: string) {
  type GuestsAdviceRoomsDocumentApi = ReturnType<typeof guestsAdviceRoomsDocumentApi>;
  type GuestsAdviceRoomsDocumentsApi = ReturnType<typeof guestsAdviceRoomsDocumentsApi>;

  function documents(): GuestsAdviceRoomsDocumentsApi;
  function documents(documentId: number): GuestsAdviceRoomsDocumentApi;
  function documents(
    documentId?: number
  ): GuestsAdviceRoomsDocumentsApi | GuestsAdviceRoomsDocumentApi {
    const apiPath = `${path}/documents`;

    return documentId
      ? guestsAdviceRoomsDocumentApi(`${apiPath}/${documentId}`)
      : guestsAdviceRoomsDocumentsApi(apiPath);
  }

  return {
    path,
    documents,

    getVirtualBackground: (authToken: string) =>
      api.get(`${path}/virtual_background?auth_token=${authToken}`),

    updateVirtualBackground: (image: File, authToken: string) =>
      api.patch(
        `${path}/virtual_background`,
        convertJsonToFormData({
          data: {
            attributes: mapFormData({
              image,
              authToken,
            }),
          },
        })
      ),

    removeVirtualBackground: (authToken: string) =>
      api.delete(`${path}/virtual_background?auth_token=${authToken}`),

    updateSummaryConsent: (authToken: string, response: boolean) =>
      api.post(`${path}/summary/consent`, {
        data: {
          attributes: {
            auth_token: authToken,
            consent: response,
          },
        },
      }),

    getWebsocketURL: (authToken: string) =>
      api.post<JsonApiResponse<{ attributes: { url: string } }>>(
        `${path}/streaming_transcribe_url`,
        {
          data: {
            attributes: {
              auth_token: authToken,
            },
          },
        }
      ),
    sendTranscription: (params: AdviceRoomTranscriptionsPostOptions) =>
      api.post<JsonApiResponseSingle>(`${path}/streaming-transcriptions`, {
        data: {
          attributes: mapFormDataDeep(params),
        },
      }),
    documentSets: () => guestClientMeetingRoomsDocumentSetsApi(`${path}/document-sets`),
    currentRecordingState: () => ({
      GET: ({ authToken }: { authToken: string }) =>
        api.get<JsonApiResponseSingle>(`${path}/current-recording-state`, {
          params: {
            '[data][attributes][auth_token]': authToken,
          },
        }),
    }),
    possibleParticipants: () =>
      guestClientMeetingRoomsPossibleParticipantsApi(`${path}/possible-participants`),
    GET: ({ authToken, include }: GuestClientMeetingRoomsGetOptions) =>
      api.get<JsonApiResponseSingle>(path, {
        params: {
          '[data][attributes][auth_token]': authToken,
          include,
        },
      }),
  };
}
